import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  FileInput,
  Group,
  Loader,
  Modal,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { Decimal } from 'decimal.js';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { z } from 'zod';
import { BankMatchModal } from './BankMatchModal.js';
import { ErrorMessage } from '../components/ErrorMessage.js';
import { translating } from '../i18n/resolver.js';
import { useT } from '../i18n/useT.js';
import type { MessageKey } from '../i18n/da.js';
import { fileToBase64 } from '../files.js';
import { formatDate, formatMoney } from '../format.js';
import {
  useAccounts,
  useBankAccounts,
  useCreateBankAccount,
  useBankTransactions,
  useImportStatement,
  useReconciliationSummary,
} from '../api/hooks.js';
import type {
  BankAccount,
  BankTransaction,
  StatementImportResult,
} from '../api/hooks.js';

/**
 * Bank — importing statements and reconciling them against the ledger.
 *
 * The reconciliation difference is at the top of the page rather than behind a
 * report link, because the Bookkeeping Act requires it to be *shown*, not
 * merely to exist. A non-zero unmatched total is the number an auditor asks
 * about, and burying it would satisfy the letter of the requirement and none of
 * its point.
 */

const STATUS_LABELS: Record<BankTransaction['status'], MessageKey> = {
  unmatched: 'bank.status.unmatched',
  draft_matched: 'bank.status.partial',
  matched: 'bank.status.matched',
};

const STATUS_COLORS: Record<BankTransaction['status'], string> = {
  unmatched: 'yellow',
  draft_matched: 'orange',
  matched: 'green',
};

export function BankPage(): ReactNode {
  const t = useT();
  const accounts = useBankAccounts();
  const [selected, setSelected] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('alle');
  const [matching, setMatching] = useState<BankTransaction | null>(null);
  const [newAccountOpened, newAccountModal] = useDisclosure(false);
  const [matchOpened, matchModal] = useDisclosure(false);

  const items = accounts.data?.items ?? [];
  // Fall back to the first account rather than showing nothing: with one bank
  // account, which is the common case, choosing it is not a decision.
  const activeAccountId = selected ?? items[0]?.id ?? null;

  const transactions = useBankTransactions({
    bankAccountId: activeAccountId ?? undefined,
    status: status === 'alle' ? undefined : (status as BankTransaction['status']),
  });

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="center" wrap="wrap">
        <Title order={2}>{t('bank.title')}</Title>
        <Button variant="light" onClick={newAccountModal.open}>
          {t('bank.newAccount')}
        </Button>
      </Group>

      <ReconciliationSummary />

      {accounts.isPending && (
        <Center p="xl">
          <Loader />
        </Center>
      )}

      {accounts.isError && <ErrorMessage error={accounts.error} />}

      {accounts.isSuccess && items.length === 0 && (
        <Alert color="blue" variant="light" title={t('bank.noAccountsTitle')}>
          {t('bank.noAccountsA')}
          <br />
          <br />
          {/*
            "6481-6489 er ledige" used to stand here on its own. It reads as
            "type 6481" and means "those numbers are free for you to create an
            account in" -- the accounts do not exist until somebody makes them.
            A real user followed it and got account_not_found.
          */}
          {t('bank.noAccountsB1')}
          <strong>{t('bank.noAccountsB2')}</strong>
          {t('bank.noAccountsB3')}
        </Alert>
      )}

      {items.length > 0 && (
        <>
          <Group align="flex-end" wrap="wrap">
            <Select
              label={t('bank.account')}
              value={activeAccountId}
              onChange={setSelected}
              allowDeselect={false}
              data={items.map((account) => ({
                value: account.id,
                label: `${account.name} (${account.accountCode})`,
              }))}
              maw={320}
            />
          </Group>

          <StatementImportPanel bankAccountId={activeAccountId} />

          <SegmentedControl
            value={status}
            onChange={setStatus}
            aria-label={t('bank.filterLabel')}
            data={[
              { value: 'alle', label: t('bank.filter.all') },
              { value: 'unmatched', label: t('bank.status.unmatched') },
              { value: 'draft_matched', label: t('bank.filter.partial') },
              { value: 'matched', label: t('bank.status.matched') },
            ]}
          />

          {transactions.isPending && (
            <Center p="xl">
              <Loader />
            </Center>
          )}

          {transactions.isError && <ErrorMessage error={transactions.error} />}

          {transactions.isSuccess && transactions.data.items.length === 0 && (
            <Text c="dimmed">{t('bank.noTransactions')}</Text>
          )}

          {transactions.isSuccess && transactions.data.items.length > 0 && (
            <Table.ScrollContainer minWidth={760}>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: 110 }}>{t('bank.col.date')}</Table.Th>
                    <Table.Th>{t('bank.col.text')}</Table.Th>
                    <Table.Th style={{ width: 130 }} className="money-cell">
                      {t('bank.col.amount')}
                    </Table.Th>
                    <Table.Th style={{ width: 130 }} className="money-cell">
                      {t('bank.col.balance')}
                    </Table.Th>
                    <Table.Th style={{ width: 150 }}>{t('bank.col.reconciliation')}</Table.Th>
                    <Table.Th style={{ width: 110 }} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {transactions.data.items.map((transaction) => (
                    <Table.Tr key={transaction.id}>
                      <Table.Td>
                        <Text size="sm">{formatDate(transaction.date)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{transaction.description ?? ''}</Text>
                        {transaction.reference !== null && (
                          <Text size="xs" c="dimmed">
                            {transaction.reference}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td className="money-cell">
                        {formatMoney(transaction.amount)}
                      </Table.Td>
                      <Table.Td className="money-cell">
                        {transaction.balanceAfter === null
                          ? ''
                          : formatMoney(transaction.balanceAfter)}
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          color={STATUS_COLORS[transaction.status]}
                          variant="light"
                          size="sm"
                        >
                          {t(STATUS_LABELS[transaction.status])}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Button
                          size="compact-sm"
                          variant="subtle"
                          onClick={() => {
                            setMatching(transaction);
                            matchModal.open();
                          }}
                        >
                          {t('bank.reconcile')}
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </>
      )}

      <NewBankAccountModal opened={newAccountOpened} onClose={newAccountModal.close} />
      <BankMatchModal
        transaction={matching}
        opened={matchOpened}
        onClose={() => {
          matchModal.close();
          setMatching(null);
        }}
      />
    </Stack>
  );
}

// -----------------------------------------------------------------------------
// The difference the law requires to be shown
// -----------------------------------------------------------------------------

function ReconciliationSummary(): ReactNode {
  const t = useT();
  const summary = useReconciliationSummary();

  if (summary.isError) {
    return <ErrorMessage error={summary.error} title={t('bank.summaryFailed')} />;
  }
  if (!summary.isSuccess || summary.data.items.length === 0) {
    return null;
  }

  return (
    <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
      {summary.data.items.map((row) => {
        const outstanding = !new Decimal(row.unmatchedTotal).isZero();
        return (
          <Card key={row.bankAccountId} withBorder padding="md">
            <Stack gap={4}>
              <Text fw={600}>{row.bankAccountName}</Text>
              <Text size="xs" c="dimmed" ff="monospace">
                {row.accountCode}
              </Text>
              <Group justify="space-between" mt="xs">
                <Text size="sm">{t('bank.unmatched')}</Text>
                <Text
                  className="money-cell"
                  fw={600}
                  {...(outstanding ? { c: 'orange' } : {})}
                >
                  {formatMoney(row.unmatchedTotal)}
                </Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  {t('bank.transactionCount')}
                </Text>
                <Text size="sm" className="money-cell">
                  {String(row.unmatchedCount)}
                </Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  {t('bank.lastStatement')}
                </Text>
                <Text size="sm" className="money-cell">
                  {row.statementClosingBalance === null
                    ? '—'
                    : formatMoney(row.statementClosingBalance)}
                </Text>
              </Group>
            </Stack>
          </Card>
        );
      })}
    </SimpleGrid>
  );
}

// -----------------------------------------------------------------------------
// Importing a statement
// -----------------------------------------------------------------------------

const importSchema = z.object({
  file: z.instanceof(File, { message: 'bank.fileRequired' }),
  reader: z.string(),
});

type ImportValues = z.infer<typeof importSchema>;

/**
 * Reading a statement in.
 *
 * The file is sent **exactly as the bank exported it**: the bytes are read with
 * `arrayBuffer`, never as text. A Danske Bank export is ISO-8859-1, and
 * decoding it as UTF-8 would replace every æ, ø and å before the reader ever
 * saw the file — see `src/files.ts`.
 *
 * The result is reported in full rather than reduced to "imported". A statement
 * whose running balance does not add up is refused and says at which line; rows
 * the bank has not booked are skipped in the bank's own wording; a gap since
 * the previous statement is reported but not blocked, because gaps are
 * legitimate. Each of those is something the person needs to see.
 */
function StatementImportPanel({
  bankAccountId,
}: {
  readonly bankAccountId: string | null;
}): ReactNode {
  const t = useT();
  const importStatement = useImportStatement();
  const [result, setResult] = useState<StatementImportResult | null>(null);

  const form = useForm<ImportValues>({
    mode: 'uncontrolled',
    initialValues: { file: null as unknown as File, reader: '' },
    validate: translating(importSchema, t, 'bank'),
  });

  async function handleSubmit(values: ImportValues): Promise<void> {
    if (bankAccountId === null) {
      return;
    }
    setResult(null);
    try {
      const imported = await importStatement.mutateAsync({
        bankAccountId,
        fileName: values.file.name,
        contentBase64: await fileToBase64(values.file),
        reader: values.reader,
      });
      setResult(imported);
      form.reset();
      notifications.show({
        color: imported.alreadyImported ? 'blue' : 'green',
        title: imported.alreadyImported
          ? t('bank.alreadyImported')
          : t('bank.imported'),
        message: imported.alreadyImported
          ? t('bank.alreadyImportedDetail')
          : t('bank.importedDetail', { count: imported.imported }),
      });
    } catch {
      // Rendered inline below with its request id: a refusal here names the
      // line where the running balance stopped adding up, and that is the whole
      // message.
    }
  }

  return (
    <Card withBorder padding="md">
      <form
        noValidate
        onSubmit={form.onSubmit((values) => {
          void handleSubmit(values);
        })}
      >
        <Stack gap="sm">
          <Text fw={500}>{t('bank.import')}</Text>
          <Text size="sm" c="dimmed">
            {t('bank.importHelp')}
          </Text>

          <Group align="flex-end" wrap="wrap">
            <FileInput
              label={t('bank.file')}
              placeholder={t('bank.filePlaceholder')}
              accept=".csv,text/csv,text/plain"
              clearable
              maw={320}
              key={form.key('file')}
              {...form.getInputProps('file')}
            />
            <Select
              label={t('bank.reader')}
              description={t('bank.readerHelp')}
              placeholder={t('bank.readerPlaceholder')}
              clearable
              maw={220}
              data={[
                { value: 'nordea-csv', label: 'Nordea Business (CSV)' },
                { value: 'danske-csv', label: 'Danske Bank (CSV)' },
              ]}
              key={form.key('reader')}
              {...form.getInputProps('reader')}
            />
            <Button
              type="submit"
              loading={importStatement.isPending}
              disabled={bankAccountId === null}
              mb={4}
            >
              {t('bank.importButton')}
            </Button>
          </Group>

          {importStatement.isError && (
            <ErrorMessage
              error={importStatement.error}
              title={t('bank.importFailed')}
            />
          )}

          {result !== null && <ImportResult result={result} />}
        </Stack>
      </form>
    </Card>
  );
}

function ImportResult({ result }: { readonly result: StatementImportResult }): ReactNode {
  const t = useT();
  const gap = result.continuity.checked && result.continuity.matches === false;

  return (
    <Stack gap="xs">
      <Alert
        color={result.alreadyImported ? 'blue' : 'green'}
        variant="light"
        title={
          result.alreadyImported ? t('bank.alreadyImported') : t('bank.readTitle')
        }
      >
        <Text size="sm">
          {t('bank.readSummary', {
            reader: result.reader,
            from: formatDate(result.firstDate),
            to: formatDate(result.lastDate),
            rows: result.rowsRead,
            imported: result.imported,
            duplicates: result.duplicates,
          })}
        </Text>
      </Alert>

      {gap && (
        <Alert color="yellow" variant="light" title={t('bank.gapTitle')}>
          <Text size="sm">
            {t('bank.gap', {
              previous:
                result.continuity.previousClosingBalance === null
                  ? '—'
                  : formatMoney(result.continuity.previousClosingBalance),
              opening:
                result.continuity.openingBalance === null
                  ? '—'
                  : formatMoney(result.continuity.openingBalance),
            })}
          </Text>
        </Alert>
      )}

      {result.skipped.length > 0 && (
        <Alert color="yellow" variant="light" title={t('bank.skippedTitle')}>
          <Stack gap={2}>
            {/*
              The bank's own wording, not a paraphrase: a transaction the bank
              has not booked is not yet a fact about the account, and the reason
              it gives is the reason.
            */}
            {result.skipped.map((row) => (
              <Text key={row.line} size="sm">
                Linje {String(row.line)}: {row.reason}
              </Text>
            ))}
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}

// -----------------------------------------------------------------------------
// Registering a bank account
// -----------------------------------------------------------------------------

const bankAccountSchema = z
  .object({
    name: z.string().min(1, 'bank.nameRequired'),
    mode: z.enum(['existing', 'new']),
    accountCode: z.string(),
    newAccountCode: z.string(),
    newAccountName: z.string(),
    iban: z.string(),
  })
  .superRefine((values, ctx) => {
    // Which fields are required depends on which of the two the user picked, so
    // the rule cannot live on the fields themselves.
    if (values.mode === 'existing') {
      if (values.accountCode.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          path: ['accountCode'],
          message: 'bank.ledgerAccountRequired',
        });
      }
      return;
    }

    const code = values.newAccountCode.trim();
    if (code === '') {
      ctx.addIssue({
        code: 'custom',
        path: ['newAccountCode'],
        message: 'bank.newAccountCodeRequired',
      });
    } else if (!/^\d+$/u.test(code)) {
      ctx.addIssue({
        code: 'custom',
        path: ['newAccountCode'],
        message: 'bank.newAccountCodeDigits',
      });
    }
    if (values.newAccountName.trim() === '') {
      ctx.addIssue({
        code: 'custom',
        path: ['newAccountName'],
        message: 'bank.newAccountNameRequired',
      });
    }
  });

type BankAccountValues = z.infer<typeof bankAccountSchema>;

/**
 * The lowest number in 6481-6489 that no account in the chart uses.
 *
 * The standard chart leaves that range free and sums it into 6490 Likvide
 * beholdninger, so an account created there shows up in the reports without a
 * mapping of its own. Undefined once all nine are taken — then the number is
 * the user's to choose, and the API says so if it collides.
 */
function freeBankAccountCode(taken: ReadonlySet<string>): string | undefined {
  for (let code = 6481; code <= 6489; code += 1) {
    const candidate = String(code);
    if (!taken.has(candidate)) {
      return candidate;
    }
  }
  return undefined;
}

/**
 * Registering a bank account.
 *
 * One ledger account each, and no sharing: reconciliation compares a statement
 * with what the books say about that account, and two statements posting to one
 * ledger account would make a difference unattributable to either. The ledger
 * account cannot be changed afterwards, which the form says before it is
 * chosen.
 *
 * A second bank account therefore needs a second ledger account, and 6481-6489
 * are free in the standard chart for exactly that — which means the account it
 * needs cannot already exist. So the form either picks one that does or creates
 * one, and the API commits both rows together. It does not send the user to
 * Kontoplan and ask them to come back.
 *
 * This form is controlled, unlike the others on this page, because which fields
 * it shows depends on its own values.
 */
function NewBankAccountModal({
  opened,
  onClose,
}: {
  readonly opened: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const t = useT();
  const create = useCreateBankAccount();
  const accounts = useAccounts();
  // Inactive bank accounts still hold the ledger account they were given: the
  // uniqueness constraint does not care whether one is in use, so a code they
  // claimed could only ever come back as a 409.
  const bankAccounts = useBankAccounts({ includeInactive: true });

  const claimed = new Set(
    (bankAccounts.data?.items ?? []).map((account) => account.accountCode),
  );
  const chart = accounts.data?.items ?? [];
  const eligible = chart.filter(
    (account) =>
      account.type === 'asset' && account.isActive && !claimed.has(account.code),
  );
  const suggestion = freeBankAccountCode(new Set(chart.map((account) => account.code)));

  const form = useForm<BankAccountValues>({
    initialValues: {
      name: '',
      mode: 'existing',
      accountCode: '',
      newAccountCode: '',
      newAccountName: '',
      iban: '',
    },
    validate: translating(bankAccountSchema, t, 'bank'),
  });

  function handleMode(next: string): void {
    form.setFieldValue('mode', next as BankAccountValues['mode']);
    // Offer the next free number rather than making the user work out which of
    // 6481-6489 is still available.
    if (next === 'new' && form.values.newAccountCode === '' && suggestion !== undefined) {
      form.setFieldValue('newAccountCode', suggestion);
    }
  }

  function handleSubmit(values: BankAccountValues): void {
    const iban = values.iban.trim();
    create.mutate(
      {
        name: values.name.trim(),
        accountCode:
          values.mode === 'new' ? values.newAccountCode.trim() : values.accountCode,
        ...(values.mode === 'new'
          ? { newAccountName: values.newAccountName.trim() }
          : {}),
        ...(iban === '' ? {} : { iban }),
      },
      {
        onSuccess: (account: BankAccount) => {
          notifications.show({
            color: 'green',
            title: t('bank.accountCreated'),
            message: `${account.name} → ${account.accountCode} ${account.accountName}`,
          });
          form.reset();
          onClose();
        },
      },
    );
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t('bank.newAccountTitle')}>
      <form noValidate onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextInput
            label={t('bank.accountName')}
            placeholder={t('bank.accountNamePlaceholder')}
            {...form.getInputProps('name')}
          />

          <SegmentedControl
            value={form.values.mode}
            onChange={handleMode}
            aria-label={t('bank.ledgerMode')}
            data={[
              { value: 'existing', label: t('bank.ledgerMode.existing') },
              { value: 'new', label: t('bank.ledgerMode.new') },
            ]}
          />

          {accounts.isError && <ErrorMessage error={accounts.error} />}

          {form.values.mode === 'existing' ? (
            <Select
              label={t('bank.ledgerAccount')}
              placeholder={t('bank.ledgerAccountPlaceholder')}
              description={t('bank.ledgerAccountHelp')}
              searchable
              disabled={accounts.isPending || bankAccounts.isPending}
              nothingFoundMessage={t('bank.noEligibleAccounts')}
              data={eligible.map((account) => ({
                value: account.code,
                label: `${account.code} — ${account.name}`,
              }))}
              {...form.getInputProps('accountCode')}
            />
          ) : (
            <>
              <TextInput
                label={t('bank.newAccountCode')}
                description={t('bank.newAccountCodeHelp')}
                inputMode="numeric"
                {...form.getInputProps('newAccountCode')}
              />
              <TextInput
                label={t('bank.newAccountName')}
                placeholder={t('bank.newAccountNamePlaceholder')}
                {...form.getInputProps('newAccountName')}
              />
            </>
          )}

          <TextInput
            label={t('bank.iban')}
            placeholder={t('bank.ibanPlaceholder')}
            {...form.getInputProps('iban')}
          />

          {create.isError && (
            <ErrorMessage error={create.error} title={t('bank.accountFailed')} />
          )}

          <Group justify="flex-end">
            <Button variant="subtle" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={create.isPending}>
              {t('common.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
